import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { DashboardTreeService, CreateNewProjectRequest } from '../../services/dashboard-tree.service';
import { ProjectDetailsDto } from '../../services/project-details.service';
import { LookupService } from '../../services/lookup.service';
import { UserSearchDialogComponent, UserSearchDialogResult } from '../../shared/user-search-dialog/user-search-dialog.component';
import { AlternateCustomersDialogComponent } from '../../shared/alternate-customers-dialog/alternate-customers-dialog.component';
import { LoggedUserService } from '../../services/logged-user.service';

export enum ProjectTypes {
    Slurry = 1,
    Spacer = 2,
    NonLab = 3,
}
export enum ProjectStatuses {
    Draft = 1,
    SenttoLab = 2,
    OngoinginLab = 3,
    FinishedinLab = 4,
    Hold = 5,
    Completed = 6,
    Ongoing = 7,
    DraftAgain = 8,
    SentToLabAgain = 9,
}
type CreateNewProjectDraft = Partial<Omit<ProjectDetailsDto, 'projectType' | 'status'>> & {
    projectType?: ProjectTypes;
    status?: ProjectStatuses;
};


@Component({
    selector: 'app-create-new-project-dialog',
    standalone: true,
    imports: [MaterialModule, FormsModule, SpinnerComponent],
    templateUrl: './create-new-project-dialog.component.html',
    styleUrls: ['./create-new-project-dialog.component.css'],
})
export class CreateNewProjectDialogComponent implements OnInit, OnChanges {
    @Input() projectIdInput: number | null = null;
    @Input() wellIdInput: number = 0;
    @Input() wellNameInput: string = '';
    @Input() businessUnitCodeInput: string = '';
    @Input() businessUnitNameInput: string = '';
    @Output() completed = new EventEmitter<number>();
    @Output() cancelled = new EventEmitter<void>();
    @Output() backToDashboard = new EventEmitter<void>();

    wellId = 0;
    wellName = '';
    businessUnitCode = '';
    businessUnitName = '';
    isLoading = true;
    isSaving = false;
    activeTab = 'project';

    get isDueDateInPast(): boolean {
        if (!this.project?.expectedDueDate) return false;
        const today = new Date().toISOString().substring(0, 10);
        return this.project.expectedDueDate < today;
    }

    private readonly emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    private isInvalidEmail(value: string | null | undefined): boolean {
        const v = (value ?? '').trim();
        return !!v && !this.emailRegex.test(v);
    }

    get receivedByError(): boolean { return this.isInvalidEmail(this.project.receivedBy); }
    get assignedToError(): boolean { return this.isInvalidEmail(this.project.assignedToCAI); }
    get requestorError(): boolean { return this.isInvalidEmail(this.project.requestorCAI); }
    get hasEmailErrors(): boolean { return this.receivedByError || this.assignedToError || this.requestorError; }

    project: CreateNewProjectDraft = {};
    formattedReceiveDate = '';

    rigOptions: any[] = [];
    serviceCompanies: any[] = [];
    mudTypes: any[] = [];
    jobTypes: any[] = [];
    jobSubTypes: any[] = [];

    readonly projectTypeLabels: Record<number, string> = {
        [ProjectTypes.Slurry]: 'Slurry',
        [ProjectTypes.Spacer]: 'Spacer',
        [ProjectTypes.NonLab]: 'NonLab',
    };

    readonly projectStatusLabels: Record<number, string> = {
        [ProjectStatuses.Draft]: 'Draft',
        [ProjectStatuses.SenttoLab]: 'SenttoLab',
        [ProjectStatuses.OngoinginLab]: 'OngoinginLab',
        [ProjectStatuses.FinishedinLab]: 'FinishedinLab',
        [ProjectStatuses.Hold]: 'Hold',
        [ProjectStatuses.Completed]: 'Completed',
        [ProjectStatuses.Ongoing]: 'Ongoing',
        [ProjectStatuses.DraftAgain]: 'DraftAgain',
        [ProjectStatuses.SentToLabAgain]: 'SentToLabAgain',
    };

    // Project type dropdown options
    projectTypeOptions: { id: number; name: string }[] = [
        { id: ProjectTypes.Slurry, name: 'Slurry' },
        { id: ProjectTypes.Spacer, name: 'Spacer' },
        { id: ProjectTypes.NonLab, name: 'Non-Lab' }
    ];

    get projectTypeDisplay(): string {
        const typeId = Number(this.project.projectType);
        return this.projectTypeLabels[typeId] ?? '';
    }

    get projectStatusDisplay(): string {
        const statusId = Number(this.project.status);
        return this.projectStatusLabels[statusId] ?? '';
    }

    canEditProjectType(): boolean {
        // Enable dropdown only for Draft status
        return this.project.status === ProjectStatuses.Draft || this.project.status === ProjectStatuses.DraftAgain;
    }

    constructor(
        private router: Router,
        private treeService: DashboardTreeService,
        private lookupService: LookupService,
        private dialog: MatDialog,
        private loggedUserService: LoggedUserService,
    ) { }

    ngOnInit(): void {
        this.initFromInputs();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Reinitialize when the target well changes (user picks a different well from the tree)
        if (changes['wellIdInput'] && !changes['wellIdInput'].firstChange) {
            this.initFromInputs();
        }
    }

    private initFromInputs(): void {
        if ((!this.wellIdInput || this.wellIdInput === 0) && !this.businessUnitCodeInput) {
            return;
        }
        this.wellId = this.wellIdInput;
        this.wellName = this.wellNameInput;
        this.businessUnitCode = this.businessUnitCodeInput;
        this.businessUnitName = this.businessUnitNameInput;
        this.isLoading = true;

        // this.projectId = Number(this.projectIdInput ?? this.route.snapshot.queryParamMap.get('projectId'));
        // if (!this.projectId) {
        //     this.closeCreateProjectView();
        //     return;
        // }
        forkJoin({
            //project: this.projectDetailsService.getProjectById(this.projectId),
            rigs: this.lookupService.getRigs(),
            serviceCompanies: this.lookupService.getServiceCompanies(),
            mudTypes: this.lookupService.getMudTypes(),
            jobTypes: this.lookupService.getJobTypes(),
            jobSubTypes: this.lookupService.getJobSubTypes(),
        }).subscribe({
            next: (results) => {
                this.rigOptions = results.rigs;
                this.serviceCompanies = results.serviceCompanies;
                this.mudTypes = results.mudTypes;
                this.jobTypes = results.jobTypes;
                this.jobSubTypes = results.jobSubTypes;
                //this.project = this.createDraftProject(results.project);
                this.project = this.createDraftProject(this.wellId, this.wellName, this.businessUnitCode, this.businessUnitName);
                this.formattedReceiveDate = this.project.receiveDate
                    ? new Date(this.project.receiveDate).toLocaleDateString()
                    : '';
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            },
        });
    }

    private createDraftProject(wellId: number, wellName: string, businessUnitCode: string, businessUnitName: string): CreateNewProjectDraft {
        const today = new Date().toISOString().substring(0, 10);
        const defaultRig = this.rigOptions?.[0];

        return {
            businessUnitCode: businessUnitCode ?? '',
            businessUnitName: businessUnitName ?? '',
            wellId: wellId,
            wellName: wellName ?? '',
            wellLocation: '',
            receiveDate: today,
            expectedDueDate: today,
            name: '',
            status: ProjectStatuses.Draft,
            projectType: ProjectTypes.Slurry,
            receivedBy: this.loggedUserService.getCurrentEmail() ?? '',
            assignedToCAI: '',
            requestorCAI: '',
            alternateCustomers: [],
            rigId: defaultRig?.id,
            rigName: defaultRig?.rigName ?? defaultRig?.name ?? '',
            customerProvidedWBS: '',
            sap: '',
            isPayAsYouGo: false,
            serviceCompanyId: this.serviceCompanies?.[0]?.id ?? null,
            notes: '',
            mudTypeId: this.mudTypes?.[0]?.id,
            jobTypeId: this.jobTypes?.[0]?.id,
            jobSubTypeId: this.jobSubTypes?.[0]?.id,
            isBlendTest: false,
            riserless: false,
            measuredDepth: undefined,
            totalVerticalDepth: undefined,
            pipeSize: '',
            openHoleSize: '',
            bhct: undefined,
            bhst: undefined,
            mudWeightPerGallon: undefined,
            waterDepth: undefined,
        };
    }

    openCaiSearch(field: 'receivedBy' | 'assignedToCAI' | 'requestorCAI'): void {
        const fieldLabels: Record<string, string> = {
            receivedBy: 'Received By',
            assignedToCAI: 'Assigned To',
            requestorCAI: 'Customer',
        };
        const ref = this.dialog.open(UserSearchDialogComponent, {
            data: { fieldLabel: fieldLabels[field] },
            width: '1400px',
            maxWidth: '95vw',
            disableClose: false,
        });
        ref.afterClosed().subscribe((result: UserSearchDialogResult | undefined) => {
            if (result) {
                this.project[field] = result.email;
            }
        });
    }

    openAlternateCustomersDialog(): void {
        const existing = this.normalizeAlternateCustomers(this.project.alternateCustomers as string[] | string | null | undefined);
        const ref = this.dialog.open(AlternateCustomersDialogComponent, {
            data: { customers: existing },
            width: '520px',
            disableClose: true,
        });

        ref.afterClosed().subscribe((result: string[] | undefined) => {
            if (!result) {
                return;
            }
            this.project.alternateCustomers = result;
        });
    }

    get alternateCustomersDisplay(): string {
        return this.normalizeAlternateCustomers(this.project.alternateCustomers as string[] | string | null | undefined).join(', ');
    }

    private normalizeAlternateCustomers(value: string[] | string | null | undefined): string[] {
        if (Array.isArray(value)) {
            return value.map((item) => (item ?? '').trim()).filter((item) => !!item);
        }

        if (typeof value === 'string' && value.trim()) {
            return value
                .split(',')
                .map((item) => item.trim())
                .filter((item) => !!item);
        }

        return [];
    }

    onCreate(): void {
        const request: CreateNewProjectRequest = {
            //projectId: this.projectId,
            ////businessunit: this.project.businessUnitCode ?? '',
            wellId: this.project.wellId!,
            receivedDate: this.project.receiveDate ?? '',
            projectType: this.project.projectType ?? ProjectTypes.Slurry,
            projectStatusId: this.project.status ?? ProjectStatuses.Draft,
            receivedBy: this.project.receivedBy ?? '',
            assignedToCAI: this.project.assignedToCAI ?? '',
            requestorCAI: this.project.requestorCAI ?? '',
            alternateCustomers: this.normalizeAlternateCustomers(this.project.alternateCustomers as string[] | string | null | undefined).join(','),
            expectedDueDate: this.project.expectedDueDate,
            rigId: this.project.rigId,
            rigName: this.rigOptions.find(r => String(r.id) === String(this.project.rigId))?.rigName
                ?? this.rigOptions.find(r => String(r.id) === String(this.project.rigId))?.name
                ?? '',
            customerProvidedWBS: this.project.customerProvidedWBS ?? '',
            sap: this.project.sap ?? '',

            isPayAsYouGo: this.project.isPayAsYouGo,

            serviceCompanyId: this.project.serviceCompanyId ?? null,
            notes: this.project.notes ?? '',
            mudTypeId: this.project.mudTypeId!,
            jobTypeId: this.project.jobTypeId!,
            jobSubTypeId: this.project.jobSubTypeId!,

            isBlendTest: this.project.isBlendTest ?? false,

            riserless: this.project.riserless ?? false,
            measuredDepth: this.project.measuredDepth, //cementMD
            totalVerticalDepth: this.project.totalVerticalDepth, //cementTVD
            pipeSize: this.project.pipeSize ?? '',
            openHoleSize: this.project.openHoleSize ?? '',
            bhct: this.project.bhct,
            bhst: this.project.bhst,
            mudWeightPerGallon: this.project.mudWeightPerGallon,
            waterDepth: this.project.waterDepth,
        };

        this.isSaving = true;
        this.treeService.createNewProject(request).subscribe({
            next: (response: any) => {
                this.isSaving = false;
                this.completed.emit(response?.id);
            },
            error: () => {
                this.isSaving = false;
            },
        });
    }

    onCancel(): void {
        this.closeCreateProjectView();
    }

    private closeCreateProjectView(): void {
        if (this.projectIdInput) {
            this.cancelled.emit();
            return;
        }
        this.router.navigate(['/projects-dashboard']);
    }
}
